// src/components/Chat/ChatBox.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { ProjectContext } from "../../context/ProjectProvider";
import { getChatCitationAPI, cancelChatRunAPI } from "../../utils/api";
import { msalInstance } from "../../utils/msalConfig";
import "./ChatBox.css"; // Crea o adapta estilos según necesites
import { triggerGlobalError } from "../../context/ErrorContext";

const getAccessToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("No active MSAL account found");

  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: [
      "https://deepextractions.onmicrosoft.com/3c98d832-da8b-410d-889c-b8c769452a7c/Task.Write"
    ],
    account
  });
  return accessToken;
};

const ChatBox = () => {
const {
  selectedProject,
  chatSessionsByProject,
  setChatSessionsByProject,
  reloadChatSession,
  chatSessionIsLoading,
  presetChatInput,
  setPresetChatInput,
  //openErrorPopup,           // ← ✔ coma añadida
} = useContext(ProjectContext);
  

  const [inputMessage, setInputMessage] = useState("");
  const [activeCitationText, setActiveCitationText] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const textareaRef = useRef(null);           // 🆕 ref
  const [isStopping, setIsStopping] = useState(false);
  const chatMessagesRef = useRef(null);

  const [loadingCitationIndex, setLoadingCitationIndex] = useState(null);

  const abortControllerRef = useRef(null);
  const chatStateRef = useRef(null);

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    requestAnimationFrame(() => {
      e.target.style.height = 'auto';
      e.target.style.height = Math.min(e.target.scrollHeight, 110) + 'px';
      
    });  
  };

  const popupRef = useRef(null);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setActiveCitationText(null);
    }
  };
  if (activeCitationText) {
    document.addEventListener("mousedown", handleClickOutside);
  }
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [activeCitationText]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();          // evita el salto de línea
      if (!isStreaming) handleSendMessage();
    }
  };  

  useEffect(() => {
    chatStateRef.current = chatSessionsByProject;
  }, [chatSessionsByProject]);
  
  useEffect(() => {
    if (!isCollapsed && chatMessagesRef.current) {
      const el = chatMessagesRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [chatSessionsByProject?.messages?.data?.length, isCollapsed]);
  
  useEffect(() => {
    if (presetChatInput) {
      setInputMessage(presetChatInput);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 110) + "px";
      }
      setPresetChatInput(""); // limpiar tras usar
    }
  }, [presetChatInput, setPresetChatInput]);  

  const renderWithCitations = (
    text,
    annotations = [],
    {
      projectId,
      chatSessionId,
      runId,
    }
  ) => {
    if (!annotations.length) return [text];
  
    const elements = [];
    let currentIndex = 0;
  
    annotations.forEach((anno, idx) => {
      const { start_index, end_index, text: annotationText, citation_index } = anno;
  
      if (currentIndex < start_index) {
        elements.push(text.slice(currentIndex, start_index));
      }
  
      const handleClick = async () => {
        setLoadingCitationIndex(citation_index);
        try {
          const data = await getChatCitationAPI(
            projectId,
            chatSessionId,
            citation_index,
            runId
          );
          setActiveCitationText(data.citation.text);
        } catch (err) {
          console.error(`❌ Error fetching citation ${citation_index}:`, err);
        } finally {
          setLoadingCitationIndex(null);
        }
      };
      
  
      elements.push(
        <button
          key={`citation-${idx}`}
          onClick={handleClick}
          style={{
            border: '1px solid #aaa',
            backgroundColor: '#f0f0f0',
            margin: '0 2px',
            cursor: 'pointer',
            fontSize: '0.9em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '20px',
            minHeight: '20px',
            padding: '2px 6px',
          }}
        >
          {loadingCitationIndex === citation_index ? (
            <div className="spinner-border spinner-border-sm" role="status" />
          ) : (
            annotationText
          )}
        </button>
      );
  
      currentIndex = end_index;
    });
  
    if (currentIndex < text.length) {
      elements.push(text.slice(currentIndex));
    }
  
    return elements;
  };
  
  // Funcion que maneja cad chunk
  const onMessageUpdate = (deltaData) => {
    if (deltaData.new_message) {
      // Step 1: Insert shell for new assistant message
      setChatSessionsByProject((prev) => {
        if (!prev || !prev.messages) return prev;

        const now = Date.now();
        const dummyId = `not_established_${now}`;
  
        const newMessageShell = {
          id: deltaData.message_id,
          run_id: deltaData.run_id,
          content: [
            {
              type: "text",
              text: {
                annotations: [],
                value: "", // Start empty, to be filled by text_delta
              },
            },
          ],
          role: "assistant",
          assistant_id: dummyId
        };
  
        return {
          ...prev,
          messages: {
            ...prev.messages,
            data: [...prev.messages.data, newMessageShell],
          },
        };
      });
    } else if (deltaData.text_delta) {
      //console.log("entered text_delta handler. deltaData.text_delta: ", deltaData.text_delta)
      // Step 2: Append delta chunk to existing assistant message
      const current = chatStateRef.current;
      if (!current || !current.messages || !deltaData.message_id) return;

      const updatedMessages = current.messages.data.map((msg) => {
        if (msg.id !== deltaData.message_id) return msg;

        const updatedContent = [...msg.content];
        const textBlock = updatedContent.find((block) => block.type === "text");

        if (textBlock && textBlock.text) {
          textBlock.text.value += deltaData.text_delta;
          if (deltaData.annotations_delta) {
            //console.log("received annotation delta: ", deltaData.annotations_delta)
            textBlock.text.annotations = textBlock.text.annotations.concat(deltaData.annotations_delta);
          }
          
        }

        return {
          ...msg,
          content: updatedContent,
        };
      });

      const updatedState = {
        ...current,
        messages: {
          ...current.messages,
          data: updatedMessages,
        },
      };

      setChatSessionsByProject(updatedState);
      chatStateRef.current = updatedState;

    }
  }

  // Maneja el envío de mensajes (placeholder)
  const handleSendMessage = async () => {
    // Abort if message is empty or no project/session selected
    if (!inputMessage.trim() || !chatSessionsByProject) return;
  
  /* ⇢ “activar” el collapse-button
     Si el panel está cerrado lo abrimos;
     si ya estaba abierto, se queda como está.*/

    if (isCollapsed) setIsCollapsed(false);

    const newMessage = {
      id: `not_established_${Date.now()}`,
      role: "user",
      content: [
        {
          type: "text",
          text: {
            value: inputMessage.trim(),
            annotations: [],
          },
        },
      ],
      created_at: `not_established_${Date.now()}`,
      assistant_id: null,
      attachments: [],
      metadata: {},
      object: "thread.message",
      run_id: null,
      thread_id: chatSessionsByProject.thread_id,
    };
  
    // Update messages locally (if using state)
    const updatedMessages = [
      ...chatSessionsByProject.messages.data,
      newMessage,
    ];
  
    // Update the state (depends on how you're managing it)
    setChatSessionsByProject((prev) => ({
      ...prev,
      messages: {
        ...prev.messages,
        data: updatedMessages,
      },
    }));
  
    setInputMessage("");
      if (textareaRef.current) {                // 🆕 reset
      textareaRef.current.style.height = "39px";
    }

    // Optional: send message to an API here
    const accessToken = await getAccessToken();
    const body = {
      role: 'user',
      text: newMessage.content[0].text.value,
      thread_id: chatSessionsByProject.thread_id
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
    
    try {
      abortControllerRef.current = new AbortController();
      setIsStreaming(true);

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/me/projects/${selectedProject.id}/chat-sessions/${chatSessionsByProject.id}/send-message`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal
      });

      //console.log("response: ", response)

      if (!response.ok) {
          // --- Si la respuesta YA viene con status 429 —
      if (response.status === 429) {
        try {
          const errorData = await response.json();
          const msg =
            errorData?.error ||
            errorData?.message ||
            "You have exceeded your quota.";
          triggerGlobalError(msg, 429);
        } catch (err) {
          triggerGlobalError("Too many requests", 429); // fallback
        }
        setIsStreaming(false);
        return;
      }

          throw new Error(`HTTP error with send-message (${response.status})`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8')
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const parts = buffer.split('#1&');
        buffer = parts.pop();
        for (const jsonString of parts) {
          if (jsonString.trim() === '') continue;
          try {
            const parsedChunk = JSON.parse(jsonString);
            //console.log("parsed chunk: ", parsedChunk)
            if (parsedChunk.new_message) {
              onMessageUpdate({
                new_message: true,
                message_id: parsedChunk.message_id,
                run_id: parsedChunk.run_id
              });
            }
            if (parsedChunk.text_delta) {
              onMessageUpdate({
                text_delta: parsedChunk.text_delta,
                message_id: parsedChunk.message_id,
                annotations_delta: parsedChunk.annotations_delta
              })
            }
          } catch (error) {
            console.error('Error parsing JSON string: ', jsonString, error)
          }
        }
      }
      setIsStreaming(false)
    } catch (error) {
      // --- Abort por 429 dentro del stream (backend lo envió después) —
    if (error?.response?.status === 429 || error?.status === 429) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Too many requests";
      triggerGlobalError(msg, 429);
    }
      else {
        console.error('Error en el stream de chat:', error);
      }
    }
  };

  const handleStopStreaming = async (project_id, chat_session_id, thread_id) => {
    if (isStreaming && abortControllerRef.current) {
      setIsStopping(true); // <- Muestra el spinner
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
      
      try {
        await cancelChatRunAPI(project_id, chat_session_id, thread_id);
        await reloadChatSession(project_id, chat_session_id);
      } catch (e) {
        console.error("Error stopping or reloading chat session:", e);
      } finally {
        setIsStopping(false); // <- Oculta el spinner cuando termina
      }
    }
  };


  
  

  return (
    <div className="chat-box-container">
      {chatSessionIsLoading && <p>Loading chat session...</p>}
      {chatSessionsByProject ? (
        <>
          {/* 🆕 Oculta o muestra según isCollapsed */}
          <div
            className="chat-messages"
            ref={chatMessagesRef}
            style={{ display: isCollapsed ? "none" : "block" }}
          >
            {chatSessionsByProject.messages.data.length > 0 ? (
              <>
                {chatSessionsByProject.messages.data.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.role}`}>
                    <span>
                      {
                        msg.content
                          .filter((block) => block.type === "text")
                          .map((block, idx) => (
                            <p key={idx}>
                              {renderWithCitations(
                                block.text?.value,
                                block.text?.annotations,
                                {
                                  projectId: selectedProject.id,
                                  chatSessionId: chatSessionsByProject.id,
                                  runId: msg.run_id,
                                }
                              )}
                            </p>
                          ))
                      }
                    </span>
                  </div>
                ))}

                {/* ✅ Mensaje de expiración */}
                {chatSessionsByProject.vs_expired && (
                  <div className="chat-message expired-warning">
                    <p>
                      The chat session's vector store has expired. If you'd like to chat again, please press <i className="bi bi-arrow-clockwise"></i> to refresh your chat session.
                       
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p>No messages.</p>
            )}
          </div>
  
          <div className="chat-input-container">
          <textarea
            value={inputMessage}
            onChange={handleInputChange}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="chat-textarea"
            rows={1}          /* opcional, refuerza la altura mínima */
            ref={textareaRef}
          ></textarea>
          <button
            className="send-button"
            onClick={
              isStreaming && !isStopping
                ? () => handleStopStreaming(
                    selectedProject.id,
                    chatSessionsByProject.id,
                    chatSessionsByProject.thread_id
                  )
                : handleSendMessage
            }
            title={
              isStopping ? "Stopping..." : isStreaming ? "Stop response" : "Send message"
            }
            disabled={isStopping} // <- evita doble clic
          >
            {isStopping ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : isStreaming ? (
              <i className="bi bi-stop-fill stop-icon"></i>
            ) : (
              <i className="bi bi-arrow-down-left-square rotated-icon"></i>
            )}
          </button>
          
          {/* ───────────── Botón de colapso ───────────── */}
          <button
            className="collapse-button"
            onClick={() => setIsCollapsed((c) => !c)}
            title={isCollapsed ? "Expand messages" : "Collapse messages"}
          >
            {isCollapsed ? (
              <i className="bi bi-arrow-bar-up"></i>
            ) : (
              <i className="bi bi-arrow-bar-down"></i>
            )}
          </button>
          </div>
        </>
      ) : (
        <p>Select a project to start the chat.</p>
      )}
  
      {/* 💬 Floating citation box */}
      {activeCitationText && (
  <div
    className="citation-popup"
    ref={popupRef}
    title="Click outside to close"
  >
    <pre>{activeCitationText}</pre>
  </div>
)}
    </div>
  );
  
};

export default ChatBox;
